/**
 * Componentes compartidos SEC EDGAR — versión muy visual e intuitiva.
 * Usado por: inversiones.js (pestaña Análisis SEC) y inteligencia.js (sección SEC).
 */
import { h, apiClient, btn } from './_common.js';

// ============================================================
// Catálogo de formularios SEC
// ============================================================
export const SEC_FORM_INFO = {
  '10-K':         { icon: '📊', label: 'Informe anual',              desc: 'Resumen financiero COMPLETO del año. El documento más importante para entender la salud real de la empresa.', color: 'gold',   impact: 'alto' },
  '10-Q':         { icon: '📈', label: 'Informe trimestral',         desc: 'Ingresos, beneficios y deuda del trimestre. Suele mover la cotización al día siguiente.',                       color: 'blue',   impact: 'alto' },
  '8-K':          { icon: '📰', label: 'Evento relevante',           desc: 'Algo importante ha pasado: cambio de CEO, adquisición, resultados preliminares, dividendo, demanda.',          color: 'green',  impact: 'medio-alto' },
  '4':            { icon: '👤', label: 'Movimiento de directivo',    desc: 'Un directivo o consejero ha comprado o vendido acciones SUYAS. Compras = confianza. Ventas masivas = cautela.', color: 'red',    impact: 'medio' },
  '144':          { icon: '🔔', label: 'Aviso de venta restringida', desc: 'Un insider AVISA que pronto venderá acciones restringidas. Suele preceder a un Form 4 días después.',          color: 'orange', impact: 'bajo' },
  'SCHEDULE 13G': { icon: '🏛️', label: 'Gran accionista (pasivo)',   desc: 'Un mega-fondo (Vanguard, BlackRock…) declara que posee más del 5%. Sin intención de control.',                color: 'purple', impact: 'medio' },
  'SCHEDULE 13D': { icon: '⚡', label: 'Gran accionista (activista)', desc: 'Un inversor con >5% que SÍ quiere influir en la empresa. Suele provocar cambios.',                            color: 'red',    impact: 'alto' },
  'S-1':          { icon: '🆕', label: 'Registro de oferta',         desc: 'La empresa va a emitir nuevas acciones o salir a bolsa (IPO).',                                                color: 'cyan',   impact: 'alto' },
  'DEF 14A':      { icon: '🗳️', label: 'Convocatoria junta anual',   desc: 'Documentación para que los accionistas voten en la junta anual.',                                              color: 'gray',   impact: 'bajo' },
  'SC 13G/A':     { icon: '🏛️', label: 'Mega-fondo actualiza',       desc: 'Un gran accionista institucional actualiza su posición (sube o baja del 5%).',                                 color: 'purple', impact: 'medio' },
  'SC 13D/A':     { icon: '⚡', label: 'Activista actualiza',         desc: 'Un accionista activista actualiza su posición o intenciones.',                                                color: 'red',    impact: 'alto' },
};

function formInfo(form) {
  const key = (form || '').toUpperCase().trim();
  return SEC_FORM_INFO[key] || { icon: '📄', label: form || 'Documento', desc: 'Otro tipo de documento presentado ante la SEC.', color: 'gray', impact: 'bajo' };
}

function impactBadge(impact) {
  const map = {
    'alto':       { bg: 'bg-red-500/20',    text: 'text-red-300',    label: '● Impacto alto' },
    'medio-alto': { bg: 'bg-orange-500/20', text: 'text-orange-300', label: '● Impacto medio-alto' },
    'medio':      { bg: 'bg-yellow-500/20', text: 'text-yellow-300', label: '● Impacto medio' },
    'bajo':       { bg: 'bg-base-700',      text: 'text-ink-300',    label: '○ Impacto bajo' },
  };
  const s = map[impact] || map['bajo'];
  return h('span', { class: `${s.bg} ${s.text} text-[10px] px-2 py-0.5 rounded-full font-medium` }, s.label);
}

// ============================================================
// Helpers visuales
// ============================================================
function fmtBigUsd(n) {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(1) + 'B $';
  if (abs >= 1e6) return (n / 1e6).toFixed(1) + 'M $';
  if (abs >= 1e3) return (n / 1e3).toFixed(1) + 'k $';
  return n.toFixed(0) + ' $';
}

function interpretGrowth(n, type) {
  // type: 'revenue' | 'profit' | 'margin'
  if (n == null || isNaN(n)) return { tag: '—', cls: 'text-ink-400' };
  if (type === 'margin') {
    if (n >= 40) return { tag: 'Excelente', cls: 'text-green-300' };
    if (n >= 25) return { tag: 'Bueno',     cls: 'text-green-300' };
    if (n >= 15) return { tag: 'Aceptable', cls: 'text-yellow-300' };
    return                  { tag: 'Bajo',  cls: 'text-red-300' };
  }
  if (n > 15)  return { tag: 'Crecimiento fuerte', cls: 'text-green-300' };
  if (n > 5)   return { tag: 'Crecimiento',        cls: 'text-green-300' };
  if (n > -5)  return { tag: 'Estable',            cls: 'text-yellow-300' };
  if (n > -15) return { tag: 'Decrecimiento',      cls: 'text-orange-300' };
  return                { tag: 'Caída fuerte',      cls: 'text-red-300' };
}

function scoreBig(score) {
  const map = {
    bullish: { bg: 'bg-green-500/20',  text: 'text-green-300',  border: 'border-green-500/40',  emoji: '🟢', label: 'BULLISH',  desc: 'Señales positivas dominan' },
    bearish: { bg: 'bg-red-500/20',    text: 'text-red-300',    border: 'border-red-500/40',    emoji: '🔴', label: 'BEARISH',  desc: 'Señales negativas dominan' },
    warning: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/40', emoji: '⚠️', label: 'WARNING',  desc: 'Atención: posibles riesgos' },
    neutral: { bg: 'bg-base-700/40',   text: 'text-ink-300',    border: 'border-base-600',      emoji: '⚪', label: 'NEUTRAL',  desc: 'Sin señales claras' },
  };
  return map[score] || map.neutral;
}

// ============================================================
// Card grande: cabecera empresa + score
// ============================================================
function renderCompanyHeader(company, ticker, cik, analysis) {
  const s = scoreBig(analysis?.score || 'neutral');
  return h('div', { class: `bg-base-900/60 border ${s.border} rounded-2xl p-5 mb-4` }, [
    h('div', { class: 'flex items-start justify-between gap-4 flex-wrap' }, [
      // Izquierda: nombre + ticker
      h('div', { class: 'flex-1 min-w-[200px]' }, [
        h('div', { class: 'flex items-center gap-3 mb-1' }, [
          h('span', { class: 'text-4xl' }, '🏢'),
          h('div', {}, [
            h('h2', { class: 'text-2xl font-bold leading-tight' }, company || ticker),
            h('div', { class: 'text-xs text-ink-400 font-mono' }, `${ticker} · CIK ${cik}`),
          ]),
        ]),
      ]),
      // Derecha: score grande
      h('div', { class: `${s.bg} ${s.text} rounded-xl px-4 py-3 text-center` }, [
        h('div', { class: 'text-3xl' }, s.emoji),
        h('div', { class: 'font-black text-base mt-1' }, s.label),
        h('div', { class: 'text-[10px] opacity-80 mt-1' }, s.desc),
      ]),
    ]),
    analysis?.scoreReason ? h('div', { class: 'mt-3 pt-3 border-t border-base-700 text-sm text-ink-200 leading-relaxed' }, [
      h('strong', { class: 'text-gold-300' }, '💡 Análisis automático: '),
      analysis.scoreReason,
    ]) : null,
  ].filter(Boolean));
}

// ============================================================
// Métricas fundamentales — 4 cards grandes con valor + interpretación
// ============================================================
function renderFundamentalsGrid(f) {
  const cards = [
    {
      icon: '📈',
      title: 'Ingresos',
      mainValue: fmtBigUsd(f.revenueLatest),
      growthN: f.revenueGrowthPct,
      type: 'revenue',
      help: 'Cuánto vendió la empresa el último trimestre vs el anterior',
    },
    {
      icon: '💰',
      title: 'Beneficio neto',
      mainValue: fmtBigUsd(f.netIncomeLatest),
      growthN: f.netIncomeGrowthPct,
      type: 'profit',
      help: 'Ganancia limpia tras pagar todo (costes, impuestos, etc.)',
    },
    {
      icon: '📊',
      title: 'Margen bruto',
      mainValue: f.grossMargin != null ? f.grossMargin.toFixed(1) + '%' : '—',
      growthN: f.grossMargin,
      type: 'margin',
      help: 'Cuánto se queda la empresa de cada euro vendido',
    },
    {
      icon: '💳',
      title: 'Deuda largo plazo',
      mainValue: fmtBigUsd(f.longTermDebt),
      growthN: null,
      type: 'revenue',
      help: 'Deuda total que la empresa debe pagar en >1 año',
    },
  ];

  return h('div', { class: 'mb-4' }, [
    h('h3', { class: 'text-sm font-semibold text-ink-300 mb-2 flex items-center gap-2' }, '💼 Salud financiera (último trimestre)'),
    h('div', { class: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3' }, cards.map((c) => {
      const interp = c.growthN != null ? interpretGrowth(c.growthN, c.type) : null;
      const growthStr = c.growthN != null && c.type !== 'margin'
        ? (c.growthN > 0 ? `+${c.growthN.toFixed(1)}% ▲` : `${c.growthN.toFixed(1)}% ▼`)
        : null;
      return h('div', { class: 'bg-base-900/60 border border-base-700 rounded-xl p-4 hover:bg-base-900/80 transition' }, [
        h('div', { class: 'flex items-center gap-2 mb-2' }, [
          h('span', { class: 'text-2xl' }, c.icon),
          h('span', { class: 'text-xs text-ink-400 uppercase tracking-wide font-medium' }, c.title),
        ]),
        h('div', { class: 'text-2xl font-black mb-1' }, c.mainValue),
        growthStr ? h('div', { class: `text-sm font-bold ${interp.cls}` }, growthStr) : null,
        interp ? h('div', { class: `text-xs ${interp.cls} font-medium` }, interp.tag) : null,
        h('p', { class: 'text-[10px] text-ink-400 mt-2 leading-snug' }, c.help),
      ].filter(Boolean));
    })),
  ]);
}

// ============================================================
// Insider activity — visual con semáforo
// ============================================================
function renderInsiderPanel(ins) {
  const sells7 = ins?.sellsLast7d || 0;
  const sells90 = ins?.sellsCount90d || 0;
  let status, color, emoji;
  if (sells7 >= 5)      { status = '⚠️ Venta masiva — vigilar';     color = 'bg-red-500/20 text-red-300 border-red-500/40';       emoji = '🚨'; }
  else if (sells7 >= 3) { status = 'Ventas elevadas';                color = 'bg-orange-500/20 text-orange-300 border-orange-500/40'; emoji = '⚠️'; }
  else if (sells7 > 0)  { status = 'Algunas ventas (normal)';        color = 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40'; emoji = '👁️'; }
  else                  { status = 'Sin ventas recientes';           color = 'bg-green-500/15 text-green-300 border-green-500/40';   emoji = '✓'; }

  return h('div', { class: 'mb-4' }, [
    h('h3', { class: 'text-sm font-semibold text-ink-300 mb-2 flex items-center gap-2' }, '👤 Actividad de directivos (insider trading)'),
    h('div', { class: `border rounded-xl p-4 ${color}` }, [
      h('div', { class: 'flex items-center gap-3 mb-2' }, [
        h('span', { class: 'text-3xl' }, emoji),
        h('div', {}, [
          h('div', { class: 'font-bold text-base' }, status),
          h('div', { class: 'text-xs opacity-80' }, `${sells7} movimientos últimos 7 días · ${sells90} últimos 90 días`),
        ]),
      ]),
      h('p', { class: 'text-xs opacity-90 leading-relaxed' },
        sells7 >= 3 ? '5+ directivos vendiendo en 1 semana puede ser señal de que algo no va bien dentro. No es definitivo, pero hay que vigilarlo.' :
        'Cuando muchos directivos venden a la vez, suele ser señal de que ellos saben algo que el público aún no.'),
    ]),
  ]);
}

// ============================================================
// Grandes accionistas + eventos 8-K
// ============================================================
function renderEventsPanel(a) {
  const big = [...(a.bigShareholders?.schedule13dRecent || []), ...(a.bigShareholders?.schedule13gRecent || [])];
  const events8k = a.recentEvents?.last8K || [];
  const blocks = [];

  if (big.length > 0) {
    blocks.push(h('div', { class: 'mb-4' }, [
      h('h3', { class: 'text-sm font-semibold text-ink-300 mb-2 flex items-center gap-2' }, '🏛️ Grandes accionistas recientes'),
      h('p', { class: 'text-xs text-ink-400 mb-2' }, 'Inversores institucionales (BlackRock, Vanguard, Berkshire) que han declarado >5% recientemente.'),
      h('div', { class: 'space-y-1' }, big.slice(0, 4).map((b) => h('a', {
        href: b.url, target: '_blank', rel: 'noopener',
        class: 'block bg-base-900/40 border border-base-700 border-l-4 border-l-purple-400 rounded-lg px-3 py-2 text-xs hover:bg-base-900/70 transition',
      }, [
        h('span', { class: 'text-ink-400 mr-2 font-mono' }, b.date),
        h('span', {}, b.description),
        h('span', { class: 'text-gold-400 float-right' }, 'abrir →'),
      ]))),
    ]));
  }

  if (events8k.length > 0) {
    blocks.push(h('div', { class: 'mb-4' }, [
      h('h3', { class: 'text-sm font-semibold text-ink-300 mb-2 flex items-center gap-2' }, '📰 Últimos eventos relevantes (8-K)'),
      h('p', { class: 'text-xs text-ink-400 mb-2' }, 'Anuncios importantes: cambios directivos, adquisiciones, resultados, dividendos.'),
      h('div', { class: 'space-y-1' }, events8k.slice(0, 5).map((e) => h('a', {
        href: e.url, target: '_blank', rel: 'noopener',
        class: 'block bg-base-900/40 border border-base-700 border-l-4 border-l-green-400 rounded-lg px-3 py-2 text-xs hover:bg-base-900/70 transition',
      }, [
        h('span', { class: 'text-ink-400 mr-2 font-mono' }, e.date),
        h('span', {}, e.description || '8-K'),
        h('span', { class: 'text-gold-400 float-right' }, 'abrir →'),
      ]))),
    ]));
  }

  return blocks;
}

// ============================================================
// Lista de filings — cards visuales con icono + descripción humana
// ============================================================
function filingCard(f) {
  const info = formInfo(f.form);
  const colorBorder = {
    gold:'border-l-gold-400', blue:'border-l-blue-400', green:'border-l-green-400',
    red:'border-l-red-400', orange:'border-l-orange-400', purple:'border-l-purple-400',
    cyan:'border-l-cyan-400', gray:'border-l-base-500',
  }[info.color] || 'border-l-base-500';
  return h('div', { class: `bg-base-900/40 border border-base-700 ${colorBorder} border-l-4 rounded-lg p-3 hover:bg-base-900/70 transition` }, [
    h('div', { class: 'flex items-start gap-3' }, [
      h('div', { class: 'text-3xl leading-none flex-shrink-0' }, info.icon),
      h('div', { class: 'flex-1 min-w-0' }, [
        h('div', { class: 'flex items-center gap-2 flex-wrap mb-1' }, [
          h('span', { class: 'font-semibold text-sm' }, info.label),
          h('span', { class: 'font-mono text-[10px] text-ink-400 bg-base-800 px-1.5 py-0.5 rounded' }, f.form),
          impactBadge(info.impact),
        ]),
        h('p', { class: 'text-xs text-ink-300 leading-relaxed mb-2' }, info.desc),
        h('div', { class: 'flex items-center gap-3 flex-wrap text-[11px] text-ink-400' }, [
          h('span', {}, `📅 ${f.filingDate || '—'}`),
          f.reportDate ? h('span', {}, `📆 ${f.reportDate}`) : null,
        ].filter(Boolean)),
      ]),
      h('a', { href: f.url, target: '_blank', rel: 'noopener', class: 'flex-shrink-0 bg-gold-500/20 hover:bg-gold-500/30 text-gold-300 text-xs px-3 py-1.5 rounded-lg font-medium transition' }, 'abrir →'),
    ]),
  ]);
}

function buildCountersGrid(filings) {
  const counts = {};
  for (const f of filings) {
    const k = (f.form || '').toUpperCase().trim();
    counts[k] = (counts[k] || 0) + 1;
  }
  const groups = [
    { keys: ['4'],            label: 'Movimientos directivos',  icon: '👤', color: 'text-red-300' },
    { keys: ['144'],          label: 'Avisos de venta',         icon: '🔔', color: 'text-orange-300' },
    { keys: ['10-Q','10-K'],  label: 'Informes financieros',    icon: '📊', color: 'text-blue-300' },
    { keys: ['8-K'],          label: 'Eventos relevantes',      icon: '📰', color: 'text-green-300' },
    { keys: ['SCHEDULE 13G','SCHEDULE 13D','SC 13G/A','SC 13D/A'], label: 'Grandes accionistas', icon: '🏛️', color: 'text-purple-300' },
  ];
  return groups.map((g) => {
    const total = g.keys.reduce((s, k) => s + (counts[k] || 0), 0);
    return h('div', { class: 'bg-base-900/60 border border-base-700 rounded-lg px-3 py-2 flex items-center gap-2' }, [
      h('span', { class: 'text-2xl' }, g.icon),
      h('div', {}, [
        h('div', { class: `text-xl font-black ${g.color}` }, String(total)),
        h('div', { class: 'text-[10px] text-ink-400 leading-tight uppercase' }, g.label),
      ]),
    ]);
  });
}

function renderLegendCard(form) {
  const info = SEC_FORM_INFO[form];
  return h('div', { class: 'bg-base-900/60 border border-base-700 rounded-lg p-3' }, [
    h('div', { class: 'flex items-center gap-2 mb-1' }, [
      h('span', { class: 'text-2xl' }, info.icon),
      h('div', {}, [
        h('div', { class: 'font-mono text-xs text-ink-400' }, form),
        h('div', { class: 'font-semibold text-sm' }, info.label),
      ]),
    ]),
    h('p', { class: 'text-xs text-ink-300 leading-relaxed mt-1' }, info.desc),
    h('div', { class: 'mt-2' }, [impactBadge(info.impact)]),
  ]);
}

// ============================================================
// EXPORT: renderSecExplorer — vista completa SEC
// ============================================================
export async function renderSecExplorer(host, initialTicker = 'AAPL') {
  host.innerHTML = '';

  // 1. Cabecera explicativa
  host.appendChild(h('div', { class: 'bg-blue-500/5 border border-blue-500/30 rounded-lg p-3 mb-4' }, [
    h('p', { class: 'text-sm text-ink-200 leading-relaxed' }, [
      h('strong', { class: 'text-blue-300' }, '🇺🇸 SEC EDGAR — '),
      'Documentos oficiales que las empresas cotizadas en EE.UU. están obligadas por ley a presentar. ',
      h('strong', {}, 'No son acciones'),
      ', son los papeles oficiales que cuentan qué pasa dentro de la empresa.',
    ]),
  ]));

  // 2. Buscador grande con sugerencias
  const tickerInput = h('input', {
    type: 'text', value: initialTicker, placeholder: 'AAPL, NVDA, TSLA…',
    class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-4 py-3 text-base uppercase font-mono',
  });
  const searchBtn = btn('🔍 Analizar empresa', () => doSearch());

  const suggestions = h('div', { class: 'flex flex-wrap gap-1 mb-4 mt-2' },
    ['AAPL', 'NVDA', 'MSFT', 'TSLA', 'GOOGL', 'AMZN', 'CVNA', 'ABNB', 'COIN', 'META'].map((t) => {
      const b = h('button', { class: 'bg-base-900/60 hover:bg-base-800 border border-base-700 rounded-md px-2 py-1 text-xs font-mono text-ink-300 hover:text-gold-300 transition' }, t);
      b.addEventListener('click', () => { tickerInput.value = t; doSearch(); });
      return b;
    })
  );

  host.appendChild(h('div', { class: 'mb-2' }, [
    h('label', { class: 'block text-xs text-ink-300 mb-1 font-medium' }, 'Ticker de la empresa (símbolo en bolsa US)'),
    h('div', { class: 'flex gap-2 items-stretch' }, [tickerInput, searchBtn]),
  ]));
  host.appendChild(h('div', { class: 'mb-2 text-[10px] text-ink-400' }, 'Sugerencias:'));
  host.appendChild(suggestions);

  // 3. Leyenda colapsable
  const legendOpen = { v: false };
  const legendGrid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mt-3 hidden' },
    Object.keys(SEC_FORM_INFO).map((k) => renderLegendCard(k)));
  const legendToggle = h('button', { class: 'text-xs text-ink-300 hover:text-gold-400 flex items-center gap-1 mb-4' }, '📖 ¿Qué significa cada tipo de documento? ▾');
  legendToggle.addEventListener('click', () => {
    legendOpen.v = !legendOpen.v;
    legendGrid.classList.toggle('hidden', !legendOpen.v);
    legendToggle.textContent = legendOpen.v ? '📖 Ocultar leyenda ▴' : '📖 ¿Qué significa cada tipo de documento? ▾';
  });
  host.appendChild(h('div', {}, [legendToggle, legendGrid]));

  // 4. Output: resultados
  const out = h('div');
  host.appendChild(out);

  async function doSearch(force = false) {
    const t = tickerInput.value.trim().toUpperCase();
    if (!t) return;
    out.innerHTML = '';
    out.appendChild(h('div', { class: 'bg-base-900/40 border border-base-700 rounded-xl p-8 text-center' }, [
      h('div', { class: 'animate-pulse text-3xl mb-2' }, '🔍'),
      h('p', { class: 'text-ink-300 text-sm' }, `Analizando ${t} con SEC EDGAR…`),
    ]));
    try {
      const [r, analysis] = await Promise.all([
        apiClient.get(`/api/v1/alt-data/sec/filings?ticker=${encodeURIComponent(t)}&limit=30`),
        apiClient.get(`/api/v1/investments/analysis/${encodeURIComponent(t)}${force ? '?force=1' : ''}`).catch(() => null),
      ]);
      out.innerHTML = '';

      // Cabecera empresa + score
      out.appendChild(renderCompanyHeader(r.company, t, r.cik, analysis));

      // Fundamentales
      if (analysis?.fundamentals) {
        out.appendChild(renderFundamentalsGrid(analysis.fundamentals));
        out.appendChild(renderInsiderPanel(analysis.insiders));
        renderEventsPanel(analysis).forEach((b) => out.appendChild(b));
      }

      // Counters
      out.appendChild(h('h3', { class: 'text-sm font-semibold text-ink-300 mt-4 mb-2 flex items-center gap-2' }, '📂 Documentos publicados (últimos 30)'));
      out.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-5 gap-2 mb-4' }, buildCountersGrid(r.filings)));

      // Lista filings
      out.appendChild(h('div', { class: 'space-y-2' }, r.filings.map(filingCard)));

      // Link SEC.gov
      out.appendChild(h('p', { class: 'text-center mt-4' }, [
        h('a', { href: `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${r.cik}&type=&dateb=&owner=include&count=40`, target: '_blank', rel: 'noopener', class: 'text-xs text-gold-400 hover:underline' }, 'ver lista completa en SEC.gov ↗'),
      ]));
    } catch (err) {
      out.innerHTML = '';
      out.appendChild(h('div', { class: 'bg-red-500/10 border border-red-500/30 rounded-xl p-4' }, [
        h('p', { class: 'text-red-300 text-sm' }, '❌ Error: ' + (err.message || err)),
      ]));
    }
  }

  // Auto-search inicial
  doSearch();
}
